import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HorizontalDistributionBarChart } from "../../components/charts/HorizontalDistributionBarChart";
import { MaterialZoomPanel } from "../../components/MaterialZoomPanel";
import ActivityWeightChart from "../../components/ActivityWeightChart";
import { formattedColorMaterial } from "../../components/formatted/FormattedText";
import { getSettingsData } from "../../services/settings";
import { useMaterialObjectsZoom } from "./hooks/useMaterialObjectsZoom";
import {
  DEFAULT_FINES_THRESHOLD_CM,
  FINES_MATERIAL_KEY,
  buildMaterialsWithFines,
} from "./utils/buildMaterialsWithFines";

const DISTRIBUTION_PANEL_HEIGHT = 720;

const SectionCard = ({ title, children }) => (
  <div className="flex flex-col min-w-0">
    <div className="char-section-header char-section-header--general-info !mb-0 min-w-0">
      <h2 className="char-section-header-title">{title}</h2>
    </div>
    <article className="mt-3 bg-white border border-gray-300 rounded-xl w-full min-w-0 flex-1 flex flex-col">
      {children}
    </article>
  </div>
);

const MaterialDistributionLayout = ({ title, children }) => (
  <div className="flex flex-col min-w-0 w-full">
    <div className="char-section-header char-section-header--general-info !mb-0 min-w-0">
      <h2 className="char-section-header-title">{title}</h2>
    </div>
    <div
      className="mt-3 flex flex-row gap-3 w-full min-w-0"
      style={{ height: DISTRIBUTION_PANEL_HEIGHT }}
    >
      {children}
    </div>
  </div>
);

const buildMaterialsFromApi = (chartData, labels, colors, values) =>
  (chartData || [])
    .map((count, index) => ({
      value: values?.[index],
      label: labels?.[index] ?? "",
      color: colors?.[index],
      count,
    }))
    .filter((material) => material.value != null)
    .sort((a, b) => b.count - a.count);

// One material chart paired with its "zoom" panel: clicking a bar, or
// picking a material from the panel's dropdown, keeps both sides in sync.
// Once Azure crops are loaded, objects below finesSizeThreshold move to a
// dedicated Fines bar (characterization-style); zoom thumbnails exclude them.
const MaterialDistributionSection = ({
  chartData,
  labels,
  colors,
  values,
  materialTotal,
  secondaryDataset,
  historyCycle,
}) => {
  const { t } = useTranslation();
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [finesThresholdCm, setFinesThresholdCm] = useState(
    DEFAULT_FINES_THRESHOLD_CM,
  );
  const { objectsByMaterial, isLoading } = useMaterialObjectsZoom(historyCycle);

  useEffect(() => {
    let cancelled = false;
    getSettingsData()
      .then((response) => {
        if (cancelled) return;
        const threshold = response.data?.settings?.finesSizeThreshold;
        if (threshold !== undefined && threshold !== null && threshold !== "") {
          const parsed = Number(threshold);
          if (Number.isFinite(parsed) && parsed > 0) {
            setFinesThresholdCm(parsed);
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const apiFallbackMaterials = useMemo(
    () => buildMaterialsFromApi(chartData, labels, colors, values),
    [chartData, labels, colors, values],
  );

  const { materials, zoomObjectsByMaterial, resolvedTotal } = useMemo(() => {
    if (isLoading) {
      return {
        materials: apiFallbackMaterials,
        zoomObjectsByMaterial: {},
        resolvedTotal: apiFallbackMaterials.reduce(
          (sum, material) => sum + (Number(material.count) || 0),
          0,
        ),
      };
    }
    return buildMaterialsWithFines({
      objectsByMaterial,
      thresholdCm: finesThresholdCm,
      translateLabel: t,
      colorForMaterial: formattedColorMaterial,
      apiFallbackMaterials,
    });
  }, [apiFallbackMaterials, finesThresholdCm, isLoading, objectsByMaterial, t]);

  const chartTotal = resolvedTotal || materialTotal;

  useEffect(() => {
    if (materials.length === 0) return;
    const hasSelected = materials.some(
      (material) =>
        material.value === selectedMaterial &&
        material.value !== FINES_MATERIAL_KEY,
    );
    if (hasSelected) return;
    const firstSelectable = materials.find(
      (material) => material.value !== FINES_MATERIAL_KEY,
    );
    if (firstSelectable) {
      setSelectedMaterial(firstSelectable.value);
    }
  }, [materials, selectedMaterial]);

  return (
    <MaterialDistributionLayout title={t("Material distribution")}>
      <article className="w-1/2 min-w-0 h-full bg-white border border-gray-300 rounded-xl flex flex-col overflow-hidden p-4">
        <HorizontalDistributionBarChart
          dataChart={materials.map((material) => material.count)}
          labelChart={materials.map((material) => material.label)}
          colorChart={materials.map((material) => material.color)}
          valueChart={materials.map((material) => material.value)}
          materialTotal={chartTotal}
          totalLabel={
            (chartTotal || 0) > 1
              ? t("Detected materials")
              : t("Detected material")
          }
          height={DISTRIBUTION_PANEL_HEIGHT - 64}
          secondaryDataset={secondaryDataset}
          selectedValue={selectedMaterial}
          onSelectCategory={setSelectedMaterial}
        />
      </article>
      <article className="w-1/2 min-w-0 h-full bg-white border border-gray-300 rounded-xl flex flex-col overflow-hidden p-4">
        <MaterialZoomPanel
          materials={materials}
          selectedMaterial={selectedMaterial}
          onSelectMaterial={setSelectedMaterial}
          objectsByMaterial={zoomObjectsByMaterial}
          isLoading={isLoading}
        />
      </article>
    </MaterialDistributionLayout>
  );
};

const ActivityDetailStatisticsTab = ({
  activatedSortingErrorOption,
  dataAnomalyDelivery,
  labelsAnomalyDelivery,
  colorAnomalyDelivery,
  numberOfAnomaly,
  anomalyChartTotals,
  valueAnomalyDelivery,
  dataNumberOfObject,
  labelObjectSeparate,
  valueObjectSeparate,
  numberOfObjects,
  colorObject,
  container,
  historyCycle,
  historyCyclePerFlow,
  findHistoryId,
  showWeightChart,
}) => {
  const { t } = useTranslation();

  const materialCharts = dataNumberOfObject || [];
  const hasAnomalyCharts = activatedSortingErrorOption && dataAnomalyDelivery;

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Stacked full-width (not side by side) even for multi-flow super
          containers: the zoom panel needs room and would be unreadably
          narrow next to a second chart+panel pair. */}
      {materialCharts.map((chartData, index) => (
        <MaterialDistributionSection
          key={`material-chart-${index}`}
          chartData={chartData}
          labels={labelObjectSeparate[index]}
          colors={colorObject[index]}
          values={valueObjectSeparate?.[index]}
          materialTotal={numberOfObjects[index]}
          secondaryDataset={
            hasAnomalyCharts && dataAnomalyDelivery[index]
              ? {
                  dataChart: dataAnomalyDelivery[index],
                  labelChart: labelsAnomalyDelivery[index],
                  colorChart: colorAnomalyDelivery[index],
                  valueChart: valueAnomalyDelivery?.[index],
                  materialTotal: numberOfAnomaly[index],
                  chartTotal: anomalyChartTotals?.[index],
                  totalLabel: t("Errors detected"),
                }
              : undefined
          }
          historyCycle={historyCyclePerFlow?.[index] ?? historyCycle}
        />
      ))}
      {showWeightChart && (
        <SectionCard title={t("Weight")}>
          <ActivityWeightChart
            container={container}
            historyCycle={historyCycle}
            findHistoryId={findHistoryId}
          />
        </SectionCard>
      )}
    </div>
  );
};

export default ActivityDetailStatisticsTab;
